import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { agenticUrl } from "@findemdev/findem-helpers";
import { getPresentationApiUrl } from "./presentationTransport";
import {
  ICreateExecProjectRequest,
  ICreateExecProjectCommentRequest,
  ICreateExecProjectPresentationReportRequest,
  IDeleteExecProjectCommentRequest,
  IDeleteExecProjectPresentationReportRequest,
  IExecProjectAiCompanyEvalRequest,
  IExecProjectAiCompanyEvalResponse,
  IExecProject,
  IExecProjectComment,
  IExecProjectDeleteResponse,
  IExecProjectSearchParams,
  IExecProjectSearchResponse,
  IListExecProjectCommentsRequest,
  IListExecProjectCommentsResponse,
  IListExecProjectPresentationReportsRequest,
  IListExecProjectPresentationReportsResponse,
  IListExecProjectsResponse,
  IResetExecProjectMilestonesRequest,
  ISession,
  IRequestApprovalRequest,
  IUpdateExecProjectCommentRequest,
  IUpdateExecProjectPresentationReportRequest,
  IUpdateExecProjectRequest,
  IUpdateSessionAgentParamsParams,
} from "../types/agent-pipelines";

const getJoidQuery = ({ joid }: { joid?: string }) =>
  joid ? `?${new URLSearchParams({ joid }).toString()}` : "";

const getExecProjectCommentsQuery = ({
  agent_namespace,
  joid,
  ref,
  show_resolved,
}: IListExecProjectCommentsRequest) => {
  const searchParams = new URLSearchParams({ ref });

  if (agent_namespace) {
    searchParams.set("agent_namespace", agent_namespace);
  }

  if (show_resolved !== undefined) {
    searchParams.set("show_resolved", String(show_resolved));
  }

  if (joid) {
    searchParams.set("joid", joid);
  }

  return `?${searchParams.toString()}`;
};

const getExecProjectPresentationReportsQuery = ({
  joid,
  ref,
}: IListExecProjectPresentationReportsRequest) => {
  const searchParams = new URLSearchParams({ ref });

  if (joid) {
    searchParams.set("joid", joid);
  }

  return `?${searchParams.toString()}`;
};

const getExecProjectPresentationReportPath = (presentationId: string) =>
  `/exec_project/presentations/${encodeURIComponent(presentationId)}`;

const getVisibleExecProjectComments = ({
  agentNamespace,
  comments = [],
  showResolved,
}: {
  agentNamespace: string;
  comments?: IExecProjectComment[];
  showResolved: boolean;
}) =>
  comments.filter(
    (comment) =>
      comment.agent_namespace === agentNamespace &&
      comment.status !== "deleted" &&
      (showResolved || comment.status === "open")
  );

const getExecProjectCommentsCachePatches = ({
  agentNamespace,
  comments,
  joid,
  ref,
}: {
  agentNamespace: string;
  comments?: IExecProjectComment[];
  joid?: string;
  ref: string;
}) =>
  [false, true].map((showResolved) => ({
    comments: getVisibleExecProjectComments({
      agentNamespace,
      comments,
      showResolved,
    }),
    queryArgs: {
      agent_namespace: agentNamespace,
      joid,
      ref,
      show_resolved: showResolved,
    },
  }));

export const execProjectsApi = createApi({
  reducerPath: "execProjectsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${agenticUrl}/agentic/api`,
    credentials: "include",
    prepareHeaders: (headers) => {
      headers.set("accept", "application/json");
      headers.set("content-type", "application/json");
      return headers;
    },
  }),
  tagTypes: [
    "ExecProject",
    "ExecProjectComments",
    "ExecProjectList",
    "ExecProjectPresentations",
  ],
  endpoints: (builder) => ({
    createExecProject: builder.mutation<
      IExecProject,
      ICreateExecProjectRequest
    >({
      query: (body) => ({
        url: `/exec_project${getJoidQuery({ joid: body.joid })}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ExecProjectList"],
    }),
    getExecProject: builder.query<IExecProject, { ref: string; joid?: string }>(
      {
        query: ({ ref, joid }) =>
          `/exec_project/${ref}${getJoidQuery({ joid })}`,
        providesTags: (_result, _error, { ref }) => [
          { type: "ExecProject", id: ref },
        ],
      }
    ),
    listExecProjects: builder.query<
      IListExecProjectsResponse,
      { joid?: string } | void
    >({
      query: (params) => `/exec_project${getJoidQuery({ joid: params?.joid })}`,
      providesTags: (result) =>
        result
          ? [
              ...result.projects.map(({ ref }) => ({
                type: "ExecProject" as const,
                id: ref,
              })),
              { type: "ExecProjectList" as const },
            ]
          : [{ type: "ExecProjectList" as const }],
    }),
    updateExecProject: builder.mutation<
      IExecProject,
      IUpdateExecProjectRequest
    >({
      query: (body) => ({
        url: `/exec_project${getJoidQuery({ joid: body.joid })}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        "ExecProjectList",
      ],
    }),
    deleteExecProject: builder.mutation<
      IExecProjectDeleteResponse,
      { ref: string; joid?: string }
    >({
      query: (body) => ({
        url: `/exec_project${getJoidQuery({ joid: body.joid })}`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        "ExecProjectList",
      ],
    }),
    listExecProjectComments: builder.query<
      IListExecProjectCommentsResponse,
      IListExecProjectCommentsRequest
    >({
      query: (params) => ({
        cache: "no-store",
        url: `/exec_project/comments${getExecProjectCommentsQuery(params)}`,
      }),
      providesTags: (_result, _error, { ref }) => [
        { type: "ExecProjectComments", id: ref },
      ],
    }),
    createExecProjectComment: builder.mutation<
      IExecProject,
      ICreateExecProjectCommentRequest
    >({
      query: ({ joid, ...body }) => ({
        url: `/exec_project/comments${getJoidQuery({ joid })}`,
        method: "POST",
        body,
      }),
      async onQueryStarted(
        { agent_namespace: agentNamespace, joid, ref },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;

          getExecProjectCommentsCachePatches({
            agentNamespace,
            comments: data.comments,
            joid,
            ref,
          }).forEach(({ comments, queryArgs }) => {
            dispatch(
              execProjectsApi.util.updateQueryData(
                "listExecProjectComments",
                queryArgs,
                (draft) => {
                  draft.comments = comments;
                }
              )
            );
          });
        } catch (_error) {
          // The component owns the user-facing mutation error state.
        }
      },
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
      ],
    }),
    updateExecProjectComment: builder.mutation<
      IExecProject,
      IUpdateExecProjectCommentRequest
    >({
      query: ({ joid, ...body }) => ({
        url: `/exec_project/comments${getJoidQuery({ joid })}`,
        method: "PUT",
        body,
      }),
      async onQueryStarted(
        { comment_id: commentId, joid, ref },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          const updatedComment = data.comments?.find(
            (comment) => comment.id === commentId
          );

          if (!updatedComment) {
            dispatch(
              execProjectsApi.util.invalidateTags([
                { type: "ExecProjectComments", id: ref },
              ])
            );
            return;
          }

          getExecProjectCommentsCachePatches({
            agentNamespace: updatedComment.agent_namespace,
            comments: data.comments,
            joid,
            ref,
          }).forEach(({ comments, queryArgs }) => {
            dispatch(
              execProjectsApi.util.updateQueryData(
                "listExecProjectComments",
                queryArgs,
                (draft) => {
                  draft.comments = comments;
                }
              )
            );
          });
        } catch (_error) {
          // The component owns the user-facing mutation error state.
        }
      },
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
      ],
    }),
    deleteExecProjectComment: builder.mutation<
      IExecProject,
      IDeleteExecProjectCommentRequest
    >({
      query: ({ joid, ...body }) => ({
        url: `/exec_project/comments${getJoidQuery({ joid })}`,
        method: "DELETE",
        body,
      }),
      async onQueryStarted(
        { comment_id: commentId, joid, ref },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          const deletedComment = data.comments?.find(
            (comment) => comment.id === commentId
          );

          if (!deletedComment) {
            dispatch(
              execProjectsApi.util.invalidateTags([
                { type: "ExecProjectComments", id: ref },
              ])
            );
            return;
          }

          getExecProjectCommentsCachePatches({
            agentNamespace: deletedComment.agent_namespace,
            comments: data.comments,
            joid,
            ref,
          }).forEach(({ comments, queryArgs }) => {
            dispatch(
              execProjectsApi.util.updateQueryData(
                "listExecProjectComments",
                queryArgs,
                (draft) => {
                  draft.comments = comments;
                }
              )
            );
          });
        } catch (_error) {
          // The component owns the user-facing mutation error state.
        }
      },
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
      ],
    }),
    listExecProjectPresentationReports: builder.query<
      IListExecProjectPresentationReportsResponse,
      IListExecProjectPresentationReportsRequest
    >({
      query: (params) => ({
        cache: "no-store",
        credentials: "include",
        url: `${getPresentationApiUrl(params.ref, "/exec_project/presentations")}${getExecProjectPresentationReportsQuery(
          params,
        )}`,
      }),
      providesTags: (_result, _error, { ref }) => [
        { type: "ExecProjectPresentations", id: ref },
      ],
    }),
    createExecProjectPresentationReport: builder.mutation<
      IExecProject,
      ICreateExecProjectPresentationReportRequest
    >({
      query: ({ joid, ...body }) => ({
        url: `${getPresentationApiUrl(body.ref, "/exec_project/presentations")}${getJoidQuery({ joid })}`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        { type: "ExecProjectPresentations", id: ref },
      ],
    }),
    updateExecProjectPresentationReport: builder.mutation<
      IExecProject,
      IUpdateExecProjectPresentationReportRequest
    >({
      query: ({ joid, presentation_id: presentationId, ...body }) => ({
        url: `${getPresentationApiUrl(body.ref, getExecProjectPresentationReportPath(presentationId))}${getJoidQuery({ joid })}`,
        method: "PUT",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        { type: "ExecProjectPresentations", id: ref },
      ],
    }),
    deleteExecProjectPresentationReport: builder.mutation<
      IExecProject,
      IDeleteExecProjectPresentationReportRequest
    >({
      query: ({ joid, presentation_id: presentationId, ...body }) => ({
        url: `${getPresentationApiUrl(body.ref, getExecProjectPresentationReportPath(presentationId))}${getJoidQuery({ joid })}`,
        method: "DELETE",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        { type: "ExecProjectPresentations", id: ref },
      ],
    }),
    requestApproval: builder.mutation<void, IRequestApprovalRequest>({
      query: ({ joid, ...body }) => ({
        url: `/exec_project/request_approval${getJoidQuery({ joid })}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        "ExecProjectList",
      ],
    }),
    approveExecProject: builder.mutation<void, IRequestApprovalRequest>({
      query: ({ joid, ...body }) => ({
        url: `/exec_project/approval${getJoidQuery({ joid })}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        "ExecProjectList",
      ],
    }),
    execProjectAiCompanyEval: builder.mutation<
      IExecProjectAiCompanyEvalResponse,
      IExecProjectAiCompanyEvalRequest
    >({
      query: (body) => ({
        url: `/exec_project_ai_company_eval${getJoidQuery({
          joid: body.joid,
        })}`,
        method: "POST",
        body,
      }),
    }),
    resetExecProjectMilestones: builder.mutation<
      void,
      IResetExecProjectMilestonesRequest
    >({
      query: ({ joid, ...body }) => ({
        url: `/exec_project/reset_milestones${getJoidQuery({ joid })}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { ref }) => [
        { type: "ExecProject", id: ref },
        "ExecProjectList",
      ],
    }),
    searchExecProjects: builder.query<
      IExecProjectSearchResponse,
      IExecProjectSearchParams
    >({
      query: ({
        cross_account,
        cursor,
        joid,
        page = 0,
        size = 20,
        filters,
        sort_fields,
      }) => ({
        url: `/exec_project/search${getJoidQuery({ joid })}`,
        method: "POST",
        body: {
          page,
          size,
          ...(cross_account === true ? { cross_account: true } : {}),
          ...(cross_account === true && cursor ? { cursor } : {}),
          ...(filters?.length ? { filters } : {}),
          ...(sort_fields?.length ? { sort_fields } : {}),
        },
      }),
      providesTags: [{ type: "ExecProjectList" as const }],
    }),
    updateSessionAgentParams: builder.mutation<
      ISession,
      IUpdateSessionAgentParamsParams
    >({
      query: ({ projectRef, ...params }) => ({
        url: `${projectRef ? getPresentationApiUrl(projectRef, "/sessions/update_agent_params") : "/sessions/update_agent_params"}${getJoidQuery({
          joid: params.joid,
        })}`,
        method: "POST",
        body: params,
        credentials: "include",
      }),
    }),
  }),
});

export const {
  useApproveExecProjectMutation,
  useCreateExecProjectCommentMutation,
  useCreateExecProjectMutation,
  useCreateExecProjectPresentationReportMutation,
  useDeleteExecProjectCommentMutation,
  useDeleteExecProjectMutation,
  useDeleteExecProjectPresentationReportMutation,
  useExecProjectAiCompanyEvalMutation,
  useGetExecProjectQuery,
  useListExecProjectCommentsQuery,
  useListExecProjectPresentationReportsQuery,
  useListExecProjectsQuery,
  useRequestApprovalMutation,
  useResetExecProjectMilestonesMutation,
  useSearchExecProjectsQuery,
  useUpdateExecProjectCommentMutation,
  useUpdateExecProjectMutation,
  useUpdateExecProjectPresentationReportMutation,
  useUpdateSessionAgentParamsMutation,
} = execProjectsApi;
